import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../services/api'
import { Material, Course } from '../types'
import {
  FileText, BookOpen, BarChart3, Flame, Rocket, ArrowRight,
  CheckCircle, Upload, Award, Clock, Calendar,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'

// ── helpers ────────────────────────────────────────────────────────────────


function calcStreak(recentResults: { completed_at: string }[]): number {
  if (!recentResults.length) return 0
  const days = new Set(
    recentResults.map((r) => r.completed_at.slice(0, 10))
  )
  let streak = 0
  const today = new Date()
  for (let i = 0; i < 30; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() - i)
    if (days.has(d.toISOString().slice(0, 10))) streak++
    else if (i > 0) break
  }
  return streak
}

function parseUtc(iso: string): Date {
  // DynamoDB stores UTC without 'Z'; append it so the browser treats it as UTC
  if (iso && !iso.endsWith('Z') && !iso.includes('+')) return new Date(iso + 'Z')
  return new Date(iso)
}

function timeAgo(iso: string) {
  const diff = Date.now() - parseUtc(iso).getTime()
  const m = Math.max(0, Math.floor(diff / 60000))
  if (m < 60) return `Hace ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `Hace ${h}h`
  return `Hace ${Math.floor(h / 24)} día${Math.floor(h / 24) !== 1 ? 's' : ''}`
}

function fileTypeLabel(type: string): string {
  if (!type) return 'DOC'
  if (type.includes('pdf')) return 'PDF'
  if (type.includes('wordprocessingml') || type.includes('docx')) return 'DOCX'
  if (type.includes('text')) return 'TXT'
  return (type.split('/').pop() ?? 'DOC').toUpperCase()
}

function materialStatus(m: Material) {
  const allReady =
    m.summary_status === 'ready' &&
    m.quiz_status === 'ready' &&
    m.mindmap_status === 'ready'
  const anyProcessing =
    m.summary_status === 'processing' ||
    m.quiz_status === 'processing' ||
    m.mindmap_status === 'processing'
  if (allReady) return { label: 'Completado', color: '#10B981', bg: 'rgba(16,185,129,0.12)' }
  if (anyProcessing) return { label: 'Procesando…', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' }
  return { label: 'Quiz listo', color: '#818CF8', bg: 'rgba(129,140,248,0.12)' }
}

// ── stat card ───────────────────────────────────────────────────────────────

function StatCard({
  value, label, icon: Icon, color,
}: {
  value: string | number
  label: string
  icon: React.ElementType
  color: string
}) {
  return (
    <div
      className="glass-card p-5 flex flex-col gap-3"
      style={{ minHeight: 110 }}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18` }}
      >
        <Icon className="w-4.5 h-4.5" style={{ color, width: 18, height: 18 }} />
      </div>
      <div>
        <p className="text-2xl font-bold leading-none" style={{ color: '#F1F5F9' }}>
          {value}
        </p>
        <p className="text-xs mt-1" style={{ color: '#64748B' }}>{label}</p>
      </div>
    </div>
  )
}

// ── activity item ───────────────────────────────────────────────────────────

function ActivityItem({
  icon: Icon, color, title, sub,
}: {
  icon: React.ElementType
  color: string
  title: string
  sub: string
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: `${color}18` }}
      >
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate" style={{ color: '#E2E8F0' }}>{title}</p>
        <p className="text-xs" style={{ color: '#64748B' }}>{sub}</p>
      </div>
    </div>
  )
}

// ── main component ──────────────────────────────────────────────────────────

interface RecentResult {
  result_id: string
  score: number
  total: number
  percentage: number
  completed_at: string
  material_name?: string
}

interface Stats {
  total_quizzes: number
  average_percentage: number
  recent_results: RecentResult[]
}

export default function DashboardPage() {

  const [materials, setMaterials] = useState<Material[]>([])
  const [courses, setCourses] = useState<Course[]>([])
  const [stats, setStats] = useState<Stats>({
    total_quizzes: 0,
    average_percentage: 0,
    recent_results: [],
  })
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => { loadDashboardData() }, [])

  const loadDashboardData = async () => {
    try {
      const [matsData, statsData, coursesData] = await Promise.all([
        api.getMaterials() as Promise<{ materials: Material[] }>,
        api.getResultStats() as Promise<Stats>,
        api.listCourses().catch(() => ({ courses: [] as Course[], total: 0 })),
      ])
      setMaterials(matsData.materials || [])
      setStats(statsData)
      setCourses(coursesData.courses || [])
    } catch (err) {
      console.error('Error loading dashboard:', err)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando dashboard..." />
      </div>
    )
  }

  const recentMaterials = materials.slice(0, 3)
  const streak = calcStreak(stats.recent_results)
  const readyCount = materials.filter(
    (m) => m.summary_status === 'ready' || m.quiz_status === 'ready'
  ).length

  // Nearest upcoming exam from courses (only shown if a course has a future examDate)
  const nextExamCourse = courses
    .filter((c) => c.examDate && new Date(c.examDate).getTime() >= Date.now() - 86400000)
    .sort((a, b) => new Date(a.examDate!).getTime() - new Date(b.examDate!).getTime())[0]
  const nextExamDays = nextExamCourse?.examDate
    ? Math.ceil((new Date(nextExamCourse.examDate).getTime() - Date.now()) / 86400000)
    : null

  // Build activity feed from recent results + recent materials
  const activityItems = [
    ...stats.recent_results.slice(0, 3).map((r) => ({
      id: r.result_id,
      icon: CheckCircle,
      color: '#10B981',
      title: `Quiz completado: ${r.percentage}%`,
      sub: timeAgo(r.completed_at),
    })),
    ...materials.slice(0, 2).map((m) => ({
      id: m.materialId,
      icon: Upload,
      color: '#818CF8',
      title: `Nuevo archivo: ${m.title}`,
      sub: m.createdAt ? timeAgo(m.createdAt) : 'Recientemente',
    })),
  ]
    .sort(() => 0) // keep order: quizzes first, then uploads
    .slice(0, 4)

  if (activityItems.length === 0 && materials.length > 0) {
    activityItems.push({
      id: 'tip',
      icon: Award,
      color: '#F59E0B',
      title: 'Insignia: Primeros pasos',
      sub: 'Subiste tu primer material',
    })
  }

  return (
    <div className="space-y-6 max-w-[1280px]">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          value={materials.length}
          label="Materiales subidos"
          icon={FileText}
          color="#818CF8"
        />
        <StatCard
          value={stats.total_quizzes * 15}
          label="Preguntas respondidas"
          icon={BookOpen}
          color="#34D399"
        />
        <StatCard
          value={`${stats.average_percentage}%`}
          label="Promedio general"
          icon={BarChart3}
          color="#A78BFA"
        />
        <StatCard
          value={`${streak} día${streak !== 1 ? 's' : ''}`}
          label="Racha activa"
          icon={Flame}
          color="#FB923C"
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Materiales recientes (2/3 width) */}
        <div className="lg:col-span-2 glass-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Materiales Recientes</h2>
            <Link
              to="/materials"
              className="text-sm font-medium flex items-center gap-1"
              style={{ color: '#818CF8' }}
            >
              Ver todos <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {recentMaterials.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: 'rgba(79,70,229,0.1)' }}
              >
                <FileText className="w-6 h-6" style={{ color: '#4F46E5' }} />
              </div>
              <p className="text-sm mb-4" style={{ color: '#64748B' }}>No tienes materiales aún</p>
              <Link to="/materials" className="btn-primary text-sm">Subir primer material</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentMaterials.map((m) => {
                const status = materialStatus(m)
                const canStudy = m.summary_status === 'ready' || m.quiz_status === 'ready'
                return (
                  <Link
                    key={m.materialId}
                    to={canStudy ? `/study/${m.materialId}` : '/materials'}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', textDecoration: 'none', display: 'flex' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; e.currentTarget.style.background = 'rgba(79,70,229,0.05)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(79,70,229,0.12)' }}
                    >
                      <FileText className="w-4 h-4" style={{ color: '#818CF8' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#E2E8F0' }}>
                        {m.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(129,140,248,0.15)', color: '#818CF8' }}
                        >
                          {fileTypeLabel(m.type)}
                        </span>
                        {m.createdAt && (
                          <span className="text-xs flex items-center gap-1" style={{ color: '#475569' }}>
                            <Clock className="w-3 h-3" />
                            {timeAgo(m.createdAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                      style={{ background: status.bg, color: status.color }}
                    >
                      {status.label}
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        {/* Right column (1/3 width) */}
        <div className="space-y-4">
          {/* Próximo examen — solo aparece si hay curso con fecha futura */}
          {nextExamCourse && nextExamDays !== null && (
            <Link
              to={`/courses/${nextExamCourse.courseId}`}
              className="block rounded-2xl p-5 transition-all"
              style={{
                background: nextExamDays <= 7
                  ? 'linear-gradient(135deg, rgba(244,63,94,0.18), rgba(251,113,133,0.10))'
                  : 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(251,191,36,0.10))',
                border: `1px solid ${nextExamDays <= 7 ? 'rgba(244,63,94,0.3)' : 'rgba(245,158,11,0.3)'}`,
                textDecoration: 'none',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{
                  background: nextExamDays <= 7 ? 'rgba(244,63,94,0.2)' : 'rgba(245,158,11,0.2)',
                }}
              >
                <Calendar className="w-5 h-5" style={{ color: nextExamDays <= 7 ? '#FB7185' : '#FBBF24' }} />
              </div>
              <p className="text-xs font-semibold tracking-wider mb-1" style={{ color: '#94A3B8' }}>
                PRÓXIMO EXAMEN
              </p>
              <h3 className="font-semibold truncate" style={{ color: '#F1F5F9' }}>
                {nextExamCourse.name}
              </h3>
              <p className="text-sm mt-1" style={{ color: nextExamDays <= 7 ? '#FB7185' : '#FBBF24' }}>
                {nextExamDays > 0
                  ? `En ${nextExamDays} día${nextExamDays === 1 ? '' : 's'}`
                  : nextExamDays === 0 ? 'Hoy' : 'Pasó'}
              </p>
            </Link>
          )}

          {/* Continuar estudiando CTA */}
          <div
            className="rounded-2xl p-5"
            style={{
              background: 'linear-gradient(135deg, rgba(79,70,229,0.25), rgba(139,92,246,0.18))',
              border: '1px solid rgba(99,102,241,0.3)',
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(79,70,229,0.25)' }}
            >
              <Rocket className="w-5 h-5" style={{ color: '#818CF8' }} />
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#E2E8F0' }}>Continuar Estudiando</h3>
            <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
              {readyCount > 0
                ? `Tienes ${readyCount} material${readyCount !== 1 ? 'es' : ''} listo${readyCount !== 1 ? 's' : ''} para estudiar`
                : 'Sube un material para empezar'}
            </p>
            <Link
              to="/materials"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)' }}
            >
              Ir a estudiar <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Actividad reciente */}
          <div className="glass-card p-5">
            <h3 className="text-xs font-semibold tracking-wider mb-4" style={{ color: '#475569' }}>
              ACTIVIDAD RECIENTE
            </h3>
            {activityItems.length === 0 ? (
              <p className="text-sm" style={{ color: '#475569' }}>Sin actividad reciente</p>
            ) : (
              <div className="space-y-4">
                {activityItems.map((item) => (
                  <ActivityItem key={item.id} {...item} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
